import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Users, Award, BookOpen, Play, Star, Target, Zap, Shield, Trophy, Briefcase, Globe } from 'lucide-react';
import { api } from '../../api';

interface Stat {
  id: string;
  number: string;
  label: string;
}

interface Formation {
  id: string;
  title: string;
  description: string;
  duration: string;
  level: string;
  price: string;
  image: string;
}

interface HomeTestimonial {
  id: string;
  name: string;
  role: string;
  content: string;
  rating: number;
}

const ShowcaseHome: React.FC = () => {
  const [stats, setStats] = useState<Stat[]>([]);
  const [formations, setFormations] = useState<Formation[]>([]);
  const [testimonials, setTestimonials] = useState<HomeTestimonial[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        console.log('Frontend: Fetching /siteContent/stats');
        const statsPromise = api.get('/siteContent/stats');
        console.log('Frontend: Fetching /siteContent/formations');
        const formationsPromise = api.get('/siteContent/formations');
        console.log('Frontend: Fetching /siteContent/homeTestimonials');
        const testimonialsPromise = api.get('/siteContent/homeTestimonials');

        const [statsResponse, formationsResponse, testimonialsResponse] = await Promise.all([
          statsPromise,
          formationsPromise,
          testimonialsPromise,
        ]);

        console.log('Frontend: Received stats response:', statsResponse.data);
        console.log('Frontend: Received formations response:', formationsResponse.data);
        console.log('Frontend: Received home testimonials response:', testimonialsResponse.data);
        setStats(statsResponse.data);
        setFormations(formationsResponse.data);
        setTestimonials(testimonialsResponse.data);
      } catch (err) {
        setError('Failed to fetch home page content.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return <div>Loading home page content...</div>;
  }

  if (error) {
    return <div>Error: {error}</div>;
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-gray-900 via-gray-400 to-black text-white overflow-hidden">
        <div className="absolute inset-0 bg-black opacity-50"></div>
        <div className="absolute inset-0 bg-gradient-to-r from-[#FFB639]/20 to-transparent"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="space-y-4">
                <h1 className="text-4xl lg:text-6xl font-bold leading-tight">
                  Développez vos compétences, libérez tout votre 
                  <span className="text-[#FFB639]"> potentiel</span> professionnel
                </h1>
                <p className="text-xl lg:text-2xl text-gray-300 leading-relaxed">
                  Des formations concrètes pour tous les métiers : du numérique à la gestion, en passant par le sport. 
                  Commencez dès aujourd'hui, sans prérequis.
                </p>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/login"
                  className="inline-flex items-center px-8 py-4 bg-[#FFB639] text-white font-semibold rounded-lg hover:bg-[#e5a333] transition-all duration-300 transform hover:scale-105 shadow-lg"
                >
                  Accéder à la plateforme
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
                <button className="inline-flex items-center px-8 py-4 border-2 border-white text-white font-semibold rounded-lg hover:bg-white hover:text-gray-900 transition-all duration-300">
                  <Play className="mr-2 h-5 w-5" />
                  Voir la démo
                </button>
              </div>

              <div className="flex items-center space-x-6 text-sm">
                <div className="flex items-center">
                  <CheckCircle className="h-5 w-5 text-[#FFB639] mr-2" />
                  <span></span>
                </div>
                <div className="flex items-center">
                  <CheckCircle className="h-5 w-5 text-[#FFB639] mr-2" />
                  <span>Accès à vie</span>
                </div>
              </div>
            </div>

            <div className="relative">
              <div className="relative z-10">
                <img
                  src="logo.png"
                  alt="Formation professionnelle"
                  className="rounded-2xl shadow-2xl"
                />
                <div className="absolute -bottom-6 -right-6 bg-[#FFB639] text-white p-6 rounded-xl shadow-lg">
                  <div className="text-2xl font-bold">400+</div>
                  <div className="text-sm">Clubs partenaires</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat) => (
              <div key={stat.id} className="text-center">
                <div className="text-4xl lg:text-5xl font-bold text-gray-900 mb-2">
                  {stat.number}
                </div>
                <div className="text-gray-600 font-medium">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="space-y-8">
              <div className="space-y-4">
                <h2 className="text-4xl font-bold text-gray-900">
                  FORMACADEMY, votre partenaire formation
                </h2>
                <p className="text-xl text-gray-600 leading-relaxed">
                  FORMACADEMY accompagne plusieurs centaines de professionnels avec des contenus pédagogiques de qualité, une application innovante, des conférences interactives et des parcours adaptés à chaque métier.
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 w-8 h-8 bg-[#FFB639]/15 rounded-lg flex items-center justify-center">
                    <BookOpen className="h-5 w-5 text-[#FFB639]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">Contenus pédagogiques</h3>
                    <p className="text-gray-600 text-sm">Formations créées par des experts</p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 w-8 h-8 bg-[#FFB639]/15 rounded-lg flex items-center justify-center">
                    <Zap className="h-5 w-5 text-[#FFB639]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">Application</h3>
                    <p className="text-gray-600 text-sm">Outils d'analyse performants</p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 w-8 h-8 bg-[#FFB639]/15 rounded-lg flex items-center justify-center">
                    <Users className="h-5 w-5 text-[#FFB639]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">Conférences</h3>
                    <p className="text-gray-600 text-sm">Échanges avec des professionnels</p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 w-8 h-8 bg-[#FFB639]/15 rounded-lg flex items-center justify-center">
                    <Shield className="h-5 w-5 text-[#FFB639]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">Ressources & outils</h3>
                    <p className="text-gray-600 text-sm">Équipements spécialisés</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="relative">
              <img
                src="https://images.pexels.com/photos/1752757/pexels-photo-1752757.jpeg"
                alt="Formation"
                className="rounded-2xl shadow-xl"
              />
              <div className="absolute -top-6 -left-6 bg-white p-6 rounded-xl shadow-lg">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-[#FFB639]/15 rounded-full flex items-center justify-center">
                    <Award className="h-6 w-6 text-[#FFB639]" />
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900">Certifié Qualiopi</div>
                    <div className="text-sm text-gray-600">Gage de qualité</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Formations Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Nos parcours de formation
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              FormAcademy réunit 16 formations réparties en trois univers : l'expertise sportive (GPS, charge d'entraînement, réathlétisation), les compétences professionnelles (management, data, digital, communication, finance) et l'inclusion numérique à tarif solidaire. Le détail de chaque parcours vous attend dans le catalogue.
            </p>
          </div>

          <div className="hidden" aria-hidden="true">
            {formations.map((formation) => (
              <div key={formation.id} className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2">
                <div className="relative">
                  <img
                    src={formation.image}
                    alt={formation.title}
                    className="w-full h-48 object-cover"
                  />
                  <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full text-sm font-semibold text-gray-900">
                    {formation.level}
                  </div>
                </div>
                
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-3 line-clamp-2">
                    {formation.title}
                  </h3>
                  <p className="text-gray-600 mb-4 line-clamp-3">
                    {formation.description}
                  </p>
                  
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-sm text-gray-500">{formation.duration}</span>
                    <span className="text-2xl font-bold text-[#FFB639]">{formation.price}</span>
                  </div>
                  
                  <Link
                    to="/login"
                    className="w-full bg-gray-900 text-white py-3 px-4 rounded-lg font-semibold hover:bg-gray-800 transition-colors duration-300 text-center block"
                  >
                    Découvrir la formation
                  </Link>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/formations"
              className="inline-flex items-center px-8 py-4 bg-[#FFB639] text-white font-semibold rounded-lg hover:bg-[#e5a333] transition-colors duration-300"
            >
              Voir toutes les formations
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Objectives Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Nos objectifs
            </h2>
            <p className="text-xl text-gray-600">
              Gagnez en expertise avec des méthodes concrètes et éprouvées
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-[#FFB639]/15 rounded-full flex items-center justify-center mx-auto">
                <Target className="h-8 w-8 text-[#FFB639]" />
              </div>
              <h3 className="text-xl font-bold text-gray-900">Devenez expert dans votre domaine</h3>
              <p className="text-gray-600">
                Développer les compétences techniques et scientifiques des professionnels
              </p>
            </div>

            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-[#FFB639]/15 rounded-full flex items-center justify-center mx-auto">
                <Zap className="h-8 w-8 text-[#FFB639]" />
              </div>
              <h3 className="text-xl font-bold text-gray-900">Apporter des méthodes de pointe</h3>
              <p className="text-gray-600">
                Transmettre les dernières innovations en matière d'entraînement
              </p>
            </div>

            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-[#FFB639]/15 rounded-full flex items-center justify-center mx-auto">
                <CheckCircle className="h-8 w-8 text-[#FFB639]" />
              </div>
              <h3 className="text-xl font-bold text-gray-900">Rendre les compétences accessibles</h3>
              <p className="text-gray-600">
                Permettre une application immédiate des connaissances acquises
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Skills Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Compétences à acquérir
            </h2>
            <p className="text-xl text-gray-600">
              Trois univers de compétences pour monter en puissance
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-2xl shadow-lg">
              <div className="w-12 h-12 bg-[#FFB639]/15 rounded-lg flex items-center justify-center mb-6">
                <Trophy className="w-6 h-6 text-[#FFB639]" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                Expertise sportive
              </h3>
              <p className="text-gray-600">
                Trois cursus d'expert pour les professionnels du sport : charge d'entraînement, réathlétisation et analyse des données GPS.
              </p>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-lg">
              <div className="w-12 h-12 bg-[#FFB639]/15 rounded-lg flex items-center justify-center mb-6">
                <Briefcase className="w-6 h-6 text-[#FFB639]" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                Compétences professionnelles
              </h3>
              <p className="text-gray-600">
                Management, gestion de projet, data, digital, communication et finance : les compétences attendues sur le marché du travail.
              </p>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-lg">
              <div className="w-12 h-12 bg-[#FFB639]/15 rounded-lg flex items-center justify-center mb-6">
                <Globe className="w-6 h-6 text-[#FFB639]" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                Inclusion numérique
              </h3>
              <p className="text-gray-600">
                Des parcours accessibles sans prérequis, à tarif solidaire, pour prendre en main le numérique et développer son activité.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Pourquoi choisir FORMACADEMY ?
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-[#FFB639]/15 rounded-full flex items-center justify-center mx-auto">
                <span className="text-2xl">🔄</span>
              </div>
              <h3 className="text-xl font-bold text-gray-900">Mise à jour annuelle des cursus</h3>
              <p className="text-gray-600">
                Contenu toujours à la pointe des dernières recherches et innovations
              </p>
            </div>

            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-[#FFB639]/15 rounded-full flex items-center justify-center mx-auto">
                <span className="text-2xl">💳</span>
              </div>
              <h3 className="text-xl font-bold text-gray-900">Paiement en 12 fois possible</h3>
              <p className="text-gray-600">
                Facilitez votre investissement formation avec nos solutions de paiement
              </p>
            </div>

            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-[#FFB639]/15 rounded-full flex items-center justify-center mx-auto">
                <span className="text-2xl">♾️</span>
              </div>
              <h3 className="text-xl font-bold text-gray-900">Accès à vie aux contenus</h3>
              <p className="text-gray-600">
                Une fois inscrit, accédez à vos formations quand vous voulez, où vous voulez
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Ils nous font confiance
            </h2>
            <p className="text-xl text-gray-600">
              Plus de 400 clubs et équipes nous recommandent
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial) => (
              <div key={testimonial.id} className="bg-white p-8 rounded-2xl shadow-lg">
                <div className="flex items-center mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="h-5 w-5 text-[#FFB639] " />
                  ))}
                </div>
                <p className="text-gray-600 mb-6 italic">
                  "{testimonial.content}"
                </p>
                <div>
                  <div className="font-semibold text-gray-900">{testimonial.name}</div>
                  <div className="text-sm text-gray-500">{testimonial.role}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-gray-900 to-black text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-4">
            Prêt à transformer votre parcours professionnel ?
          </h2>
          <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto">
            Rejoignez les centaines de professionnels qui ont déjà fait le choix de l'excellence avec FORMACADEMY
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/login"
              className="inline-flex items-center px-8 py-4 bg-[#FFB639] text-white font-semibold rounded-lg hover:bg-[#e5a333] transition-all duration-300 transform hover:scale-105"
            >
              Commencer maintenant
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
            <button className="inline-flex items-center px-8 py-4 border-2 border-white text-white font-semibold rounded-lg hover:bg-white hover:text-gray-900 transition-all duration-300">
              Découvrir nos formations
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ShowcaseHome;